import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { productsApi } from '../../services/api'
import TableActionButton from '../../components/TableActionButton'

interface Product {
  id: number
  name: string
  description: string | null
  presentation: string
  recommended_price: number
  category: 'A' | 'B' | 'C'
  stock_kg: number
  is_active: boolean
  created_at: string
  producto_maestro: string
}

interface ProductForm {
  name: string
  description: string
  presentation: string
  recommended_price: string
  category: 'A' | 'B' | 'C'
  producto_maestro: string
}

const emptyForm: ProductForm = {
  name: '',
  description: '',
  presentation: '',
  recommended_price: '',
  category: 'C',
  producto_maestro: '',
}

const categoryLabels: Record<string, string> = {
  A: 'Premium',
  B: 'Estándar',
  C: 'Económico',
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [form, setForm] = useState<ProductForm>(emptyForm)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const fetchProducts = async () => {
    try {
      const res = await productsApi.list()
      setProducts(res.data.products || [])
    } catch (err) {
      console.error('Error fetching products:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchProducts()
  }, [])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value })
  }

  const openNew = () => {
    setForm(emptyForm)
    setEditingId(null)
    setError('')
    setShowForm(true)
  }

  const openEdit = (p: Product) => {
    setForm({
      name: p.name,
      description: p.description || '',
      presentation: p.presentation,
      recommended_price: String(p.recommended_price),
      category: p.category,
      producto_maestro: p.producto_maestro || '',
    })
    setEditingId(p.id)
    setError('')
    setShowForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('El nombre es requerido')
      return
    }
    if (!form.presentation.trim()) {
      setError('La presentación es requerida')
      return
    }
    if (!form.recommended_price || parseFloat(form.recommended_price) < 0) {
      setError('Se requiere un precio recomendado válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name,
        description: form.description || null,
        presentation: form.presentation,
        recommended_price: parseFloat(form.recommended_price),
        category: form.category,
        producto_maestro: form.producto_maestro.trim() || null,
      }
      if (editingId) {
        await productsApi.update(editingId, payload)
        toast.success('Producto actualizado')
      } else {
        await productsApi.create(payload)
        toast.success('Producto creado')
      }
      setShowForm(false)
      fetchProducts()
    } catch (err: any) {
      const message = err.response?.data?.error || 'Error al guardar producto'
      setError(message)
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('¿Eliminar este producto del catálogo?')) return
    try {
      await productsApi.delete(id)
      toast.success('Producto eliminado')
      fetchProducts()
    } catch (err: any) {
      console.error('Error deleting product:', err)
      toast.error(err.response?.data?.error || 'Error al eliminar el producto')
    }
  }

  if (loading) {
    return <div className="text-center py-8">Cargando...</div>
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-espresso-800">Catálogo de Productos</h1>
        <button
          onClick={openNew}
          className="px-4 py-2 bg-terracotta-600 text-white rounded-md hover:bg-terracotta-700 text-sm font-medium"
        >
          + Agregar Producto
        </button>
      </div>

      <p className="text-sm text-espresso-500 mb-6">
        Gestiona el catálogo de productos y establece precios recomendados. Los vendedores seleccionarán de estos productos y establecerán sus propios precios de venta.
      </p>

      {/* Products Table */}
      {products.length === 0 ? (
        <div className="bg-cream-50 shadow rounded-lg p-8 text-center">
          <p className="text-espresso-500">No hay productos aún. Agrega tu primer producto para comenzar.</p>
        </div>
      ) : (
        <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-espresso-100">
              <thead className="bg-cream-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Nombre</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Presentación</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Categoría</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Precio Rec.</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Stock (kg)</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-espresso-500 uppercase tracking-wider">Descripción</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-espresso-500 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-cream-50 divide-y divide-espresso-100">
                {products.map((p) => (
                  <tr key={p.id} className="hover:bg-cream-100">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-espresso-800">
                      {p.name}
                      {p.producto_maestro && p.producto_maestro !== p.name && (
                        <span className="block text-xs font-normal text-espresso-500">{p.producto_maestro}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {p.presentation}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        p.category === 'A'
                          ? 'bg-terracotta-100 text-terracotta-800'
                          : p.category === 'B'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-cream-200 text-espresso-700'
                      }`}>
                        {categoryLabels[p.category] || p.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-green-700">
                      ${p.recommended_price.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-espresso-800">
                      {Number(p.stock_kg || 0).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 text-sm text-espresso-500 max-w-xs truncate">
                      {p.description || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <div className="flex items-center justify-end gap-1">
                        <TableActionButton onClick={() => openEdit(p)} variant="primary">
                          Editar
                        </TableActionButton>
                        <TableActionButton onClick={() => handleDelete(p.id)} variant="danger">
                          Eliminar
                        </TableActionButton>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-espresso-200">
              <h2 className="text-lg font-semibold text-espresso-800">
                {editingId ? 'Editar Producto' : 'Nuevo Producto'}
              </h2>
            </div>
            <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
              {error && (
                <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Nombre *</label>
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Producto maestro</label>
                <input
                  type="text"
                  name="producto_maestro"
                  value={form.producto_maestro}
                  onChange={handleChange}
                  placeholder="Se usa para agrupar el stock de distintas presentaciones del mismo producto — si lo dejas vacío, se usa el mismo nombre del producto"
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Presentación *</label>
                <select
                  name="presentation"
                  value={form.presentation}
                  onChange={handleChange}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  required
                >
                  <option value="" disabled>Selecciona una presentación</option>
                  <option value="granel">Granel</option>
                  <option value="250gr">250gr</option>
                  <option value="1kg">1kg</option>
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Precio Recomendado *</label>
                  <input
                    type="number"
                    name="recommended_price"
                    value={form.recommended_price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                    required
                  />
                  <p className="text-xs text-espresso-500 mt-1">Precio que los vendedores te pagan</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-espresso-700 mb-1">Categoría</label>
                  <select
                    name="category"
                    value={form.category}
                    onChange={handleChange}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="A">Premium (A)</option>
                    <option value="B">Estándar (B)</option>
                    <option value="C">Económico (C)</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-espresso-700 mb-1">Descripción</label>
                <textarea
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={2}
                  className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4 border-t border-espresso-200">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-espresso-300 rounded-md text-sm text-espresso-700 hover:bg-cream-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  {saving ? 'Guardando...' : editingId ? 'Actualizar' : 'Crear'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
